import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Calendar, ChevronDown, ChevronUp, DollarSign } from 'lucide-react';
import { Abonnement } from '../types/abonnement';
import { formatXOF, calculateFees, PROVIDERS, getProviderFees } from '../utils/feeCalculator';
import { Provider, Currency } from '../types/calculator';
interface UpcomingPaymentsProps {
  abonnements: Abonnement[];
}
interface PaymentWithProviders extends Abonnement {
  providerAmounts: Record<Provider, number>;
  daysUntil: number;
}
export function UpcomingPayments({
  abonnements
}: UpcomingPaymentsProps) {
  const [expandedPayments, setExpandedPayments] = useState<Set<string>>(new Set());
  const togglePayment = (id: string) => {
    const newExpanded = new Set(expandedPayments);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
    }
    setExpandedPayments(newExpanded);
  };
  const calculateDaysUntil = (nextDate: string) => {
    const today = new Date();
    const next = new Date(nextDate);
    const diff = Math.ceil((next.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
    return diff;
  };
  const formatAmount = (amount: number, currency: Currency) => {
    if (currency === 'XOF') {
      return formatXOF(amount);
    } else if (currency === 'EUR') {
      return new Intl.NumberFormat('fr-FR', {
        style: 'currency',
        currency: 'EUR'
      }).format(amount);
    } else {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD'
      }).format(amount);
    }
  };
  // Calculate provider amounts for each payment
  const paymentsWithProviders: PaymentWithProviders[] = abonnements.map(abonnement => {
    const currency = abonnement.currency as Currency;
    const amount = abonnement.amount;
    const daysUntil = calculateDaysUntil(abonnement.next_payment_date);
    const providerAmounts: Record<string, number> = {};
    const providers = Object.keys(PROVIDERS) as Provider[];
    const fees = getProviderFees();
    providers.forEach(provider => {
      const breakdown = calculateFees(amount, currency, provider);
      const providerFee = fees[provider];
      const feeAmount = providerFee === 'not_defined' ? 0 : providerFee;
      providerAmounts[provider] = breakdown.totalCost + feeAmount;
    });
    return {
      ...abonnement,
      providerAmounts: providerAmounts as Record<Provider, number>,
      daysUntil
    };
  }).filter(p => p.daysUntil >= 0) // Only upcoming payments
  .sort((a, b) => a.daysUntil - b.daysUntil); // Sort by closest first
  if (paymentsWithProviders.length === 0) {
    return null;
  }
  return <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden" data-id="element-342">
      <div className="bg-gradient-to-r from-blue-600 to-emerald-600 px-6 py-4" data-id="element-343">
        <div className="flex items-center gap-3" data-id="element-344">
          <Calendar className="w-6 h-6 text-white" data-id="element-345" />
          <div data-id="element-346">
            <h2 className="text-xl font-bold text-white" data-id="element-347">Paiements à venir</h2>
            <p className="text-blue-100 text-sm" data-id="element-348">
              Montants à préparer par provider
            </p>
          </div>
        </div>
      </div>

      <div className="divide-y divide-gray-200" data-id="element-349">
        {paymentsWithProviders.map((payment, index) => {
        const isExpanded = expandedPayments.has(payment.id);
        const isUrgent = payment.daysUntil <= 7;
        const sortedProviders = (Object.keys(payment.providerAmounts) as Provider[]).sort((a, b) => payment.providerAmounts[a] - payment.providerAmounts[b]);
        const bestProvider = sortedProviders[0];
        return <motion.div key={payment.id} initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          delay: index * 0.05
        }} className={`${isUrgent ? 'bg-orange-50' : 'bg-white'}`} data-id="element-350">
              {/* Payment Header */}
              <button onClick={() => togglePayment(payment.id)} className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors" data-id="element-351">
                <div className="flex items-center gap-4 flex-1" data-id="element-352">
                  <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${isUrgent ? 'bg-orange-200' : 'bg-blue-100'}`} data-id="element-353">
                    <DollarSign className={`w-6 h-6 ${isUrgent ? 'text-orange-700' : 'text-blue-600'}`} data-id="element-354" />
                  </div>
                  <div className="text-left flex-1" data-id="element-355">
                    <div className="flex items-center gap-3 mb-1" data-id="element-356">
                      <h3 className="font-bold text-gray-900" data-id="element-357">
                        {payment.name}
                      </h3>
                      {isUrgent && <span className="text-xs bg-orange-200 text-orange-800 px-2 py-0.5 rounded-full font-bold" data-id="element-358">
                          URGENT
                        </span>}
                    </div>
                    <div className="flex items-center gap-4 text-sm text-gray-600" data-id="element-359">
                      <span data-id="element-360">
                        {formatAmount(payment.amount, payment.currency as Currency)}
                      </span>
                      <span data-id="element-361">•</span>
                      <span data-id="element-362">
                        {payment.daysUntil === 0 ? "Aujourd'hui" : payment.daysUntil === 1 ? 'Demain' : `Dans ${payment.daysUntil} jours`}
                      </span>
                      <span data-id="element-363">•</span>
                      <span data-id="element-364">
                        {new Date(payment.next_payment_date).toLocaleDateString('fr-FR')}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-4" data-id="element-365">
                  <div className="text-right" data-id="element-366">
                    <p className="text-xs text-gray-600 mb-1" data-id="element-367">Meilleur prix</p>
                    <p className="text-lg font-bold text-emerald-600" data-id="element-368">
                      {formatXOF(payment.providerAmounts[bestProvider])}
                    </p>
                  </div>
                  {isExpanded ? <ChevronUp className="w-5 h-5 text-gray-400" data-id="element-369" /> : <ChevronDown className="w-5 h-5 text-gray-400" data-id="element-370" />}
                </div>
              </button>

              {/* Provider Breakdown */}
              {isExpanded && <motion.div initial={{
            opacity: 0,
            height: 0
          }} animate={{
            opacity: 1,
            height: 'auto'
          }} exit={{
            opacity: 0,
            height: 0
          }} className="px-6 pb-4" data-id="element-371">
                  <div className="bg-gray-50 rounded-lg p-4" data-id="element-372">
                    <p className="text-sm font-semibold text-gray-700 mb-3" data-id="element-373">
                      Montant à préparer en XOF par provider:
                    </p>
                    <div className="space-y-2" data-id="element-374">
                      {sortedProviders.map((provider, idx) => {
                  const providerInfo = PROVIDERS[provider];
                  const amount = payment.providerAmounts[provider];
                  const isBest = provider === bestProvider;
                  return <div key={provider} className={`flex items-center justify-between p-3 rounded-lg border-2 ${isBest ? 'bg-emerald-50 border-emerald-300' : 'bg-white border-gray-200'}`} data-id="element-375">
                            <div className="flex items-center gap-3" data-id="element-376">
                              <img src={providerInfo.logoUrl} alt={providerInfo.name} className="w-10 h-10 rounded-lg object-contain bg-white p-1" data-id="element-377" />
                              <div data-id="element-378">
                                <div className="flex items-center gap-2" data-id="element-379">
                                  <span className={`font-semibold ${isBest ? 'text-emerald-900' : 'text-gray-900'}`} data-id="element-380">
                                    {providerInfo.name}
                                  </span>
                                  {isBest && <span className="text-xs bg-emerald-200 text-emerald-800 px-2 py-0.5 rounded-full font-bold" data-id="element-381">
                                      LE MOINS CHER
                                    </span>}
                                </div>
                                <p className="text-xs text-gray-600" data-id="element-382">
                                  {providerInfo.country === 'ALL' ? 'Tous pays' : providerInfo.country}
                                </p>
                              </div>
                            </div>
                            <div className="text-right" data-id="element-383">
                              <p className={`text-xl font-bold ${isBest ? 'text-emerald-900' : 'text-gray-900'}`} data-id="element-384">
                                {formatXOF(amount)}
                              </p>
                              {!isBest && <p className="text-xs text-red-600 font-medium" data-id="element-385">
                                  +
                                  {formatXOF(amount - payment.providerAmounts[bestProvider])}
                                </p>}
                            </div>
                          </div>;
                })}
                    </div>
                  </div>
                </motion.div>}
            </motion.div>;
      })}
      </div>
    </div>;
}